import { useEffect, useMemo, useRef, useState } from "react";

// Small pieces the catalogue screens (countries, groups, nationalities, visa
// types, questions, documents, prices) share. They live here rather than in
// src/components because they are about the catalogue's words and data, not
// about the shell.

/**
 * An in-app delete confirm that names the record and says what goes with it.
 * "Are you sure to delete ?" with an empty name told staff nothing; the
 * standard asks for the thing, the consequence, and a verb on the button.
 */
export function confirmDelete(alert, { title, message, confirmLabel = "Delete", onConfirm }) {
  alert.show({
    title,
    message,
    type: "delete",
    actions: [
      { name: "Cancel", type: "cancel", callback: () => {} },
      { name: confirmLabel, type: "delete", callback: onConfirm },
    ],
  });
}

/**
 * What a delete question says about undoing it. A deletion the Activity log
 * records as a soft delete can be restored from its line there (by someone
 * with the Activity log's edit right); anything else is gone for good.
 * Kinds: country, product, document, question, faq, group, nationality,
 * questionCategory, visaType, staff, role, customer.
 */
const RESTORABLE = new Set(["country", "product", "document", "question", "faq", "group", "nationalityOnVisa", "nationality", "questionCategory", "visaType", "staff", "role", "customer"]);
export function undoNote(kind) {
  return RESTORABLE.has(kind) ? "You can restore it from the Activity log." : "This can’t be undone.";
}

/**
 * Closing a form with typed input in it (Escape, a click outside, Cancel)
 * asks before throwing the input away.
 */
export function confirmDiscard(alert, { title, message = "What you typed here is lost.", onDiscard }) {
  alert.show({
    title,
    message,
    actions: [
      { name: "Keep editing", type: "cancel", callback: () => {} },
      { name: "Discard", type: "delete", callback: onDiscard },
    ],
  });
}

/**
 * The public website this CMS feeds, worked out from the API it talks to:
 * api-dev.safrati.ai -> dev.safrati.ai, api.safrati.ai -> safrati.ai.
 */
export function websiteHost() {
  try {
    const host = new URL(process.env.REACT_APP_BASEURL || "").hostname;
    if (host.startsWith("api-")) return host.slice(4);
    if (host.startsWith("api.")) return host.slice(4);
    return host || "the website";
  } catch {
    return "the website";
  }
}

/** "Delete 3 questions?" / "Delete the question "Email"?" */
export function deleteTitle(noun, rows, nameOf) {
  if (rows.length === 1) {
    const name = rows[0] ? nameOf(rows[0]) : "";
    return name ? `Delete the ${noun} “${name}”?` : `Delete this ${noun}?`;
  }
  return `Delete ${rows.length} ${noun}s?`;
}

export const pickRows = (items, ids) => ids.map((id) => items.find((item) => item.id === id)).filter(Boolean);

/**
 * Search for the short lists the API returns whole (visa types, groups,
 * documents...): filtering happens in the page, so it is instant and costs no
 * request. Pass the result as DynamicTable's items.
 */
export function useLocalSearch(items, fields) {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!needle) return items;
    return items.filter((item) =>
      fields.some((field) => {
        const value = typeof field === "function" ? field(item) : item?.[field];
        return value != null && String(value).toLowerCase().includes(needle);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, needle]);
  return {
    query,
    searching: needle !== "",
    filtered,
    onSearch: (event) => setQuery(event?.target?.value ?? ""),
    emptyTitle: (fallback) => (needle ? `Nothing matches “${query.trim()}”` : fallback),
    emptyHint: (fallback) => (needle ? "Check the spelling, or clear the search to see everything." : fallback),
  };
}

/**
 * Search for the long lists the API pages (nationalities, questions). Waits
 * for a pause in typing, then calls `search(text, isCurrent)`; an empty box
 * calls `reload(isCurrent)`. `isCurrent()` is false once a newer keystroke has
 * superseded the request, so a slow answer can never overwrite a newer one —
 * before, the full list reloaded on every keystroke and could land on top of
 * the search results a second later.
 */
export function useRemoteSearch({ search, reload, delay = 400 }) {
  const [query, setQuery] = useState("");
  const timer = useRef(null);
  const seq = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);
  const needle = query.trim();
  const run = (text) => {
    clearTimeout(timer.current);
    const token = ++seq.current;
    const isCurrent = () => token === seq.current;
    if (text === "") reload(isCurrent);
    else search(text, isCurrent);
  };
  return {
    query,
    searching: needle !== "",
    /**
     * Re-read after a change (edit, delete): the search again when one is
     * typed, the full list otherwise. Calling reload directly showed every
     * row while the box still said what was being searched for.
     */
    refresh: () => run(needle),
    onSearch: (event) => {
      const value = event?.target?.value ?? "";
      setQuery(value);
      clearTimeout(timer.current);
      const token = ++seq.current;
      const isCurrent = () => token === seq.current;
      timer.current = setTimeout(() => {
        if (value.trim() === "") reload(isCurrent);
        else search(value.trim(), isCurrent);
      }, delay);
    },
    emptyTitle: (fallback) => (needle ? `Nothing matches “${needle}”` : fallback),
    emptyHint: (fallback) => (needle ? "Check the spelling, or clear the search to see everything." : fallback),
  };
}

const blank = (value) => value === null || value === undefined || String(value).trim() === "";

/** Prices are stored in US dollars: 25 → "$25", 25.5 → "$25.50". */
export function usd(value) {
  if (blank(value)) return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Rich-text descriptions shown as one readable line in a table cell. */
export function plainText(html, max = 140) {
  if (blank(html)) return "";
  let text = String(html);
  if (typeof DOMParser !== "undefined") {
    text = new DOMParser().parseFromString(text, "text/html").body.textContent || "";
  } else {
    text = text.replace(/<[^>]*>/g, " ");
  }
  text = text.replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/** "6 months" / "1 month", for durations staff type as a bare number. */
export const months = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${n} month${n === 1 ? "" : "s"}`;
};

/**
 * Some uploads come back as the literal string "None" or "null" (the API
 * stringifies a missing icon). Treat those as no image, so the table shows a
 * neutral placeholder instead of requesting a file that does not exist.
 */
export const imageOrEmpty = (value) => (blank(value) || value === "None" || value === "null" || value === "undefined" ? "" : value);
